/**
 * MenuScene - the entry screen.
 *
 * Three large mode buttons (Daily, Adventure, Quick Play). Tap or click any
 * button, or use 1/2/3 / D/A/Q hotkeys. Selection launches GameScene with
 * the appropriate RunConfig.
 *
 * Also hosts the sound on/off toggle in the corner — the only setting we have.
 * Daily streak + today's status + adventure best load from localStorage.
 */

import Phaser from 'phaser';
import { VIEWPORT_PX_W, VIEWPORT_PX_H } from '../config';
import { ADVENTURE_LIVES, PRACTICE_SEED, PRACTICE_TUNING, levelConfig, type Mode } from '../game/Mode';
import type { RunConfig } from '../game/RunConfig';
import {
  load,
  commit,
  todaysSeedString,
  getDailyEntry,
  computeStreak,
  type SaveState,
} from '../persistence/saveState';
import { SoundManager } from '../sound/SoundManager';
import { HapticsManager } from '../haptics/HapticsManager';
import { ParentBridge } from '../bridge/ParentBridge';

interface ButtonHandle {
  rect: Phaser.GameObjects.Rectangle;
  title: Phaser.GameObjects.Text;
  body: Phaser.GameObjects.Text;
}

export class MenuScene extends Phaser.Scene {
  constructor() {
    super({ key: 'Menu' });
  }

  create(): void {
    SoundManager.setScene(this);

    const w = VIEWPORT_PX_W;
    const h = VIEWPORT_PX_H;

    this.add.rectangle(w / 2, h / 2, w, h, 0x0d0d10);

    // Title
    this.add
      .text(w / 2, 70, 'SPLAT', {
        fontFamily: 'monospace',
        fontSize: '56px',
        fontStyle: 'bold',
        color: '#e8c87a',
      })
      .setOrigin(0.5);
    this.add
      .text(w / 2, 122, 'CHIMP', {
        fontFamily: 'monospace',
        fontSize: '34px',
        fontStyle: 'bold',
        color: '#7adb6b',
      })
      .setOrigin(0.5);

    this.add
      .text(w / 2, 158, 'jungle survival, one tile at a time', {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#888',
      })
      .setOrigin(0.5);

    // Buttons (annotated with persistence data)
    const state = load();
    const startY = 220;
    const btnH = 80;
    const btnGap = 12;
    this.makeButton('daily', '1', startY, btnH, this.dailyAnnotation(state));
    this.makeButton('adventure', '2', startY + (btnH + btnGap), btnH, this.adventureAnnotation(state));
    this.makeButton('quick', '3', startY + (btnH + btnGap) * 2, btnH, null);
    this.makeButton('practice', '4', startY + (btnH + btnGap) * 3, btnH, null);

    // Hotkeys (1/2/3/4 + D/A/Q/P + Enter for Daily, M sound, S stats)
    this.input.keyboard?.on('keydown', (ev: KeyboardEvent) => {
      const k = ev.key.toLowerCase();
      if (k === '1' || k === 'd' || ev.key === 'Enter') return this.start('daily');
      if (k === '2' || k === 'a') return this.start('adventure');
      if (k === '3' || k === 'q') return this.start('quick');
      if (k === '4' || k === 'p') return this.start('practice');
      if (k === 'm') this.toggleSoundFromButton();
      if (k === 's') this.scene.start('Stats');
    });

    // Sound toggle in top-right corner. Tap to mute/unmute.
    this.buildSoundToggle();
    this.buildControlToggle();
    this.buildHapticToggle();

    // Footer: streak (if any) on the left, STATS link on the right.
    const streak = computeStreak(state);
    if (streak > 0) {
      this.add
        .text(16, h - 28, `streak: ${streak} 🔥`, {
          fontFamily: 'monospace',
          fontSize: '11px',
          color: '#e8c87a',
        })
        .setOrigin(0, 0.5);
    }
    const statsLink = this.add
      .text(w - 16, h - 28, '📊 STATS', {
        fontFamily: 'monospace',
        fontSize: '12px',
        fontStyle: 'bold',
        color: '#cfcfcf',
        backgroundColor: '#00000088',
      })
      .setOrigin(1, 0.5)
      .setPadding(8, 4, 8, 4);
    statsLink.setInteractive({ useHandCursor: true });
    statsLink.on('pointerdown', () => this.scene.start('Stats'));
  }

  /** Returns null if no annotation to show; otherwise a short status line. */
  private dailyAnnotation(state: SaveState): string | null {
    const todayKey = todaysSeedString();
    const entry = getDailyEntry(state, todayKey);
    if (!entry) return null;
    if (entry.firstAttemptComplete) {
      return `today: ✓ ${entry.firstAttemptScore}  best ${entry.bestScore}`;
    }
    return `today: ${entry.attempts} attempt${entry.attempts === 1 ? '' : 's'}  best ${entry.bestScore}`;
  }

  private adventureAnnotation(state: SaveState): string | null {
    const best = state.adventureBest;
    if (best.bestTotal <= 0) return null;
    return `best ${best.bestTotal}  L${best.highestLevel}/7`;
  }

  private soundToggleText?: Phaser.GameObjects.Text;

  /** Tiny "🔊"/"🔇" toggle pinned top-right. Tap or press M to flip. */
  private buildSoundToggle(): void {
    const w = VIEWPORT_PX_W;
    const text = this.add
      .text(w - 12, 12, this.soundIcon(), {
        fontFamily: 'monospace',
        fontSize: '20px',
        backgroundColor: '#00000066',
      })
      .setOrigin(1, 0)
      .setPadding(6, 4, 6, 4);
    text.setInteractive({ useHandCursor: true });
    text.on('pointerdown', () => this.toggleSoundFromButton());
    this.soundToggleText = text;

    // Keep the label in sync if the parent (or anything else) changes mute
    // while the menu is showing. Unsubscribe on scene shutdown.
    const unsub = ParentBridge.onMuteChange(() => {
      if (this.soundToggleText) this.soundToggleText.setText(this.soundIcon());
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, unsub);
  }

  private soundIcon(): string {
    return SoundManager.enabled ? '🔊 ON' : '🔇 OFF';
  }

  private toggleSoundFromButton(): void {
    SoundManager.toggle();
    if (this.soundToggleText) this.soundToggleText.setText(this.soundIcon());
  }

  private controlToggleText?: Phaser.GameObjects.Text;

  /**
   * Touch-control scheme toggle, pinned top-right under the sound toggle.
   * Flips between swipe (default) and quadrant tap. Persisted to settings;
   * GameScene reads it when constructing the InputController.
   */
  private buildControlToggle(): void {
    const w = VIEWPORT_PX_W;
    const text = this.add
      .text(w - 12, 46, this.controlLabel(), {
        fontFamily: 'monospace',
        fontSize: '13px',
        backgroundColor: '#00000066',
        color: '#cfcfcf',
      })
      .setOrigin(1, 0)
      .setPadding(6, 4, 6, 4);
    text.setInteractive({ useHandCursor: true });
    text.on('pointerdown', () => this.toggleControlScheme());
    this.controlToggleText = text;
  }

  private currentTouchMode(): 'swipe' | 'quadrant' {
    return load().settings.touchMode ?? 'swipe';
  }

  private controlLabel(): string {
    return this.currentTouchMode() === 'swipe' ? '✋ SWIPE' : '⊹ TAP-ZONES';
  }

  private toggleControlScheme(): void {
    const next = this.currentTouchMode() === 'swipe' ? 'quadrant' : 'swipe';
    const state = load();
    state.settings.touchMode = next;
    commit(state);
    if (this.controlToggleText) this.controlToggleText.setText(this.controlLabel());
  }

  private hapticToggleText?: Phaser.GameObjects.Text;

  /**
   * Haptic feedback toggle. Only shown on devices that actually support the
   * Vibration API (so iOS users aren't presented with a dead control).
   * Pinned top-right below the control toggle.
   */
  private buildHapticToggle(): void {
    if (!HapticsManager.isSupported) return;
    const w = VIEWPORT_PX_W;
    const text = this.add
      .text(w - 12, 80, this.hapticLabel(), {
        fontFamily: 'monospace',
        fontSize: '13px',
        backgroundColor: '#00000066',
        color: '#cfcfcf',
      })
      .setOrigin(1, 0)
      .setPadding(6, 4, 6, 4);
    text.setInteractive({ useHandCursor: true });
    text.on('pointerdown', () => this.toggleHaptics());
    this.hapticToggleText = text;
  }

  private hapticLabel(): string {
    return HapticsManager.enabled ? '📳 ON' : '📳 OFF';
  }

  private toggleHaptics(): void {
    HapticsManager.toggle();
    if (this.hapticToggleText) this.hapticToggleText.setText(this.hapticLabel());
  }

  private makeButton(
    mode: Mode,
    hotkey: string,
    y: number,
    height: number,
    annotation: string | null
  ): ButtonHandle {
    const w = VIEWPORT_PX_W;
    const x = w / 2;
    const width = w - 32;

    const colors: Record<Mode, { fill: number; titleHex: string }> = {
      daily: { fill: 0x1c3a1c, titleHex: '#7adb6b' },
      adventure: { fill: 0x3a2c1c, titleHex: '#e8a55a' },
      quick: { fill: 0x1c2c3a, titleHex: '#5aa8e8' },
      practice: { fill: 0x2a2a2a, titleHex: '#cfcfcf' },
    };
    const c = colors[mode];

    const rect = this.add.rectangle(x, y, width, height, c.fill, 1);
    rect.setStrokeStyle(2, 0xffffff, 0.12);
    rect.setInteractive({ useHandCursor: true });

    const labels: Record<Mode, [string, string]> = {
      daily: ['DAILY', 'one playfield, one life,\nsame for everyone today'],
      adventure: ['ADVENTURE', '7 levels, 3 lives,\nfaster every level'],
      quick: ['QUICK PLAY', 'random playfield, one life,\nnothing on the line'],
      practice: ['PRACTICE', 'learn the rules,\nnothing recorded'],
    };
    const [title, body] = labels[mode];

    // Tag (hotkey) at the left
    this.add
      .text(x - width / 2 + 12, y, hotkey, {
        fontFamily: 'monospace',
        fontSize: '18px',
        color: '#ffffff',
        backgroundColor: '#00000040',
      })
      .setOrigin(0, 0.5)
      .setPadding(6, 2, 6, 2);

    const titleText = this.add
      .text(x, y - 16, title, {
        fontFamily: 'monospace',
        fontSize: '22px',
        fontStyle: 'bold',
        color: c.titleHex,
      })
      .setOrigin(0.5);

    const bodyText = this.add
      .text(x, y + 18, body, {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#cccccc',
        align: 'center',
        lineSpacing: 2,
      })
      .setOrigin(0.5);

    // Optional annotation in bottom-right corner of the button (best score etc).
    if (annotation) {
      this.add
        .text(x + width / 2 - 8, y + height / 2 - 6, annotation, {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: '#ffffffaa',
        })
        .setOrigin(1, 1);
    }

    // Hover/pointer feedback
    rect.on('pointerover', () => rect.setStrokeStyle(2, 0xffffff, 0.35));
    rect.on('pointerout', () => rect.setStrokeStyle(2, 0xffffff, 0.12));
    rect.on('pointerdown', () => this.start(mode));

    return { rect, title: titleText, body: bodyText };
  }

  private start(mode: Mode): void {
    const cfg = buildInitialRunConfig(mode);
    this.scene.start('Game', cfg);
  }
}

/** Build the RunConfig for a fresh start of any mode. */
export function buildInitialRunConfig(mode: Mode): RunConfig {
  if (mode === 'daily') {
    return {
      mode,
      seed: todaysSeed(),
      level: 1,
      scrollIntervalMul: 1,
      spiderTickMul: 1,
      waterMultiplier: 1,
      lives: 1,
      carryScore: 0,
    };
  }
  if (mode === 'adventure') {
    const adventureRunId = `adv-${Math.random().toString(36).slice(2, 10)}`;
    const lc = levelConfig(1);
    return {
      mode,
      seed: `${adventureRunId}::L1`,
      level: 1,
      scrollIntervalMul: lc.scrollIntervalMul,
      spiderTickMul: lc.spiderTickMul,
      waterMultiplier: lc.waterMultiplier,
      lives: ADVENTURE_LIVES,
      carryScore: 0,
      adventureRunId,
    };
  }
  if (mode === 'practice') {
    return {
      mode,
      seed: PRACTICE_SEED,
      level: 1,
      scrollIntervalMul: PRACTICE_TUNING.scrollIntervalMul,
      spiderTickMul: PRACTICE_TUNING.spiderTickMul,
      waterMultiplier: PRACTICE_TUNING.waterMultiplier,
      lives: 1, // logical lives; practice's retry path doesn't actually deduct
      carryScore: 0,
    };
  }
  // quick
  return {
    mode,
    seed: randomSeed(),
    level: 1,
    scrollIntervalMul: 1,
    spiderTickMul: 1,
    waterMultiplier: 1,
    lives: 1,
    carryScore: 0,
  };
}

function todaysSeed(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function randomSeed(): string {
  return Math.random().toString(36).slice(2, 10);
}
